import { h } from 'preact';
import Section from 'components/bolt/section/section';
import Store from 'store/store';
import 'store/bolt/gallery';
import { accessNested, sortObjectByOrder, joinClasses } from 'utils/utils';
import { arrayWithUniqueKeys } from 'utils/bolt';
import { getValueForClientSize, isVideosPage } from 'utils/bolt';
import { useDynamicAssetFolder } from 'utils/assets';
import { getPlaylistData } from 'services/boltYoutube';
import Button from 'components/bolt/button/button';
import Download from 'components/bolt/icons/download';
import ArrowDown from 'components/bolt/icons/arrowDown';
import HTML from 'components/core/html/html';
import Link from 'components/bolt/link/link';
import VideoPlayer from 'components/bolt/videos/player';
import SharePopout from 'components/bolt/header/sharePopout';
import s from 'components/bolt/gallery/gallery.sss';
import { pages } from 'pages/bolt_pages';
import * as webp from 'utils/bolt/webp';
import slugify from 'utils/slugify';

const TOUCH_SLOP = 8;

export default class Gallery extends Section {

	constructor(props) {
		super(props);
		const hasMultipleGalleries = this.hasMultipleGalleries();
		const pageData = this.props.pageData || props.appData.pages[this.props.id]?.data;
		let galleryData = pageData?.contentCarousel;

		if (this.props.isMiniGallery) {
			galleryData = accessNested(this.props, 'appData.options.paramountFeatures.toutCarousel.contentCarousel', {});
		} else if (this.props.isEmbeded) {
			galleryData = pageData?.gallery?.contentCarousel;
		}

		const galleryHash = pageData?.hash;
		const galleryDataArray = sortObjectByOrder(galleryData, (value, key) => ({
			...value,
			key: slugify(props.id + '-' + key),
			altKey: galleryHash && slugify(galleryHash + '-' + key)
		}));
		this.unpackYoutubePlaylists(galleryDataArray);

		this.state = {
			images: galleryDataArray || [],
			hasMultipleGalleries,
			fadeOut: false
		};

		this.onDragStart = this.onDragStart.bind(this);
		this.onDrag = this.onDrag.bind(this);
		this.onDragEnd = this.onDragEnd.bind(this);

		this.onImageClick = this.onImageClick.bind(this);
		this.imageLoaded = this.imageLoaded.bind(this);
		this.renderImageLoader = this.renderImageLoader.bind(this);
		this.downloadAll = this.downloadAll.bind(this);
		this.downloadAllAsSingleFile = this.downloadAllAsSingleFile.bind(this);
		this.getFallbackFormat = this.getFallbackFormat.bind(this);
		this.togglePlaying = this.togglePlaying.bind(this);
		this.videoClickHandler = this.videoClickHandler.bind(this);
		this.triggerFadeTransition = this.triggerFadeTransition.bind(this);
		this.onImageShare = this.onImageShare.bind(this);
		this.getImageForHash = this.getImageForHash.bind(this);

		this.useDownloadAttribute = typeof window === 'undefined' || ('download' in document.createElement('a'));
	}

	unpackYoutubePlaylists = (array) => {
		const foundYoutubePlaylists = array.find(obj => obj.youtubePlaylistId);
		if (foundYoutubePlaylists !== undefined) {
			this.replacePlaylistWithVideos(array);
		}
	}

	componentDidMount() {
		let images = this.state.images || [];
		const activeImage = this.getActiveImage();
		this.activeImageUrl = this.getSrc(activeImage);
		let activeImageIndex = images.findIndex(img => img.key === activeImage.key);

		if (images.length) {
			Store.emit('GALLERY_VIEW_IMAGE', images[activeImageIndex].key);
		}

		const isVideo = (activeImage.videoSrc || activeImage.vimeoId || activeImage.youtubeId
			|| activeImage.youtubeId || activeImage.disneySrc || activeImage.youtubePlaylistId);
		if (!isVideo) {
			this.startRotation();
		}
	}

	componentWillUnmount() {
		window.clearInterval(this.rotationInterval);
		window.clearTimeout(this.changeImageTimeout);
		window.clearTimeout(this.fadeTimeout);
	}

	componentDidUpdate(prevProps) {
		if (prevProps.id !== this.props.id) {
			const pageData = this.props.pageData || this.props.appData.pages[this.props.id]?.data;
			let galleryData = pageData?.contentCarousel;

			if (this.props.isMiniGallery) {
				galleryData = accessNested(this.props, 'appData.options.paramountFeatures.toutCarousel.contentCarousel', {});
			} else if (this.props.isEmbeded) {
				galleryData = pageData?.gallery?.contentCarousel;
			}
			const galleryDataArray = sortObjectByOrder(galleryData, (value, key) => ({ ...value, key: slugify(key) }));
			this.unpackYoutubePlaylists(galleryDataArray);
			Store.emit('CHANGE_ACTIVE_IMAGE', {
				image: galleryDataArray[0],
				pageId: this.props.combinedId || this.props.id
			});

			this.setState({ images: galleryDataArray });
		}
	}

	/**
	* Returns the gallery data array without playlist objects.
	*
	* @param {Array.<object>} array
	* @returns {Array.<object>} array of objects
	**/
	removeYoutubePlaylists = (array) => {
		return array.filter((item) => !item.youtubePlaylistId && item);
	}

	replacePlaylistWithVideos(videoDataArray) {
		Promise.all(videoDataArray.map(vid => {
			if (vid && vid.youtubePlaylistId) {
				// return Promise.reject();
				return this.getVideosFromPlaylist(vid);
			}
			return vid;
		})).then((data) => {
			const concatArray = [].concat(...data);
			this.setState({ images: arrayWithUniqueKeys(concatArray) });
		}).catch(() => {
			this.setState({ images: this.removeYoutubePlaylists(videoDataArray) });
		});
	}

	sortPlaylist(a, b, order) {
		if (!order) {
			return 0;
		}
		const playlistOrder = order.replace(/\s/g, '').split(',').reverse();
		return playlistOrder.indexOf(b.snippet.resourceId.videoId) - playlistOrder.indexOf(a.snippet.resourceId.videoId);
	}

	getVideosFromPlaylist(vid) {
		const playlistId = vid.youtubePlaylistId;
		const noCookies = vid.noCookies === 'No Cookie Mode';
		return getPlaylistData(playlistId).then(data => {
			return data.items.filter(item => item.snippet.thumbnails.medium).sort((a, b) => this.sortPlaylist(a, b, vid.playlistOrder)).map(item => ({
				name: item.snippet.title,
				thumbSrc: item.snippet.thumbnails.medium.url,
				youtubeId: item.snippet.resourceId.videoId,
				key: item.id,
				noCookies
			}));
		});
	}

	hasMultipleGalleries() {
		const pages = sortObjectByOrder(accessNested(this.props, 'appData.pages', {}));
		const galleryComps = pages.filter(page => page.component === 'gallery' || page.page === 'gallery' || page.data?.gallery?.contentCarousel /*embeded gallery*/ || page.type === 'filmPage');
		const hasMiniGallery = accessNested(this.props, 'appData.options.paramountFeatures.toutCarousel.contentCarousel', false);
		let totalGalleries = galleryComps && galleryComps.length;
		if (hasMiniGallery) {
			totalGalleries += 1;
		}
		return (totalGalleries > 1);
	}

	getFallbackFormat() {
		return accessNested(this.props, 'appData.pages.boltGallery.data.alternativeImageFormat', 'jpg');
	}

	getActiveImage() {
		const { images, hasMultipleGalleries } = this.state;
		const { isMiniGallery } = this.props;
		const store = Store.get();
		const activeImageFromStore = hasMultipleGalleries ?
			accessNested(store, ['gallery', isMiniGallery ? 'miniGallery' : (this.props.combinedId || this.props.id), 'activeImage']) :
			accessNested(store, 'gallery.activeImage');
		const activeImage = activeImageFromStore || images[0] || {};

		const foundInGallery = this.getImageForHash();
		if (foundInGallery) {
			return foundInGallery;
		}

		return activeImage;
	}

	getImageForHash() {
		const hash = typeof window !== 'undefined' && location.hash.slice(1);
		if (!hash) return;

		const { images } = this.state;

		return images.find((item) => {
			const selectVideoSrc = (item.vimeoId || item.youtubeId);
			const isSelectVideo = !!selectVideoSrc;

			if (isSelectVideo) {
				return (selectVideoSrc === hash || item.key === hash);
			}

			return item.key === hash || item.altKey === hash;
		});
	}

	changeActiveImage(image, opts) {
		if (opts?.userTriggered) {
			window.clearInterval(this.rotationInterval);
			this.startRotation();
		}
		const { hasMultipleGalleries } = this.state;
		const { isMiniGallery } = this.props;
		const pageId = hasMultipleGalleries && (isMiniGallery ? 'miniGallery' : this.props.id);

		if (isMiniGallery) {
			return this.triggerFadeTransition({ image, pageId: hasMultipleGalleries && (this.props.combinedId || pageId) });
		}

		const foundInGallery = this.getImageForHash();
		if (foundInGallery) {
			window.history.replaceState({}, document.title, location.query ? '.' + location.query : '.');
		}

		Store.emit('CHANGE_ACTIVE_IMAGE', { image, pageId: hasMultipleGalleries && (this.props.combinedId || pageId) });

		const isVideo = (image.videoSrc || image.vimeoId || image.youtubeId
			|| image.youtubeId || image.disneySrc || image.youtubePlaylistId);
		if (this.rotating && isVideo) {
			window.clearInterval(this.rotationInterval);
			this.rotating = false;
		} else if (!this.rotating && !isVideo) {
			this.startRotation();
		}

		const newSrc = this.getSrc(image);
		const srcHasNotChanged = (newSrc === this.activeImageUrl) && !isVideo;

		if (!isMiniGallery && !srcHasNotChanged) {
			this.setState({ loading: true });
		}

		if (newSrc && !isVideo) {
			this.activeImageUrl = this.getSrc(image);
		}
	}

	triggerFadeTransition(data) {
		this.setState({ fadeOut: true });
		this.fadeTimeout = window.setTimeout(() => {
			Store.emit('CHANGE_ACTIVE_IMAGE', data);
			this.setState({ fadeOut: false });
		}, 600);
	}

	getSrc(activeImage) {
		let src;
		if (!activeImage.mobileSrc && !activeImage.tabletSrc) {
			src = activeImage.desktopSrc;
		} else {
			src = {
				desktop: activeImage.desktopSrc,
				mobile: activeImage.mobileSrc,
				tablet: activeImage.tabletSrc
			};
		}
		return useDynamicAssetFolder(getValueForClientSize(src));
	}

	onDragStart(e) {
		const isDesktop = this.props.client?.isDesktopSize;
		const { isMiniGallery } = this.props;
		if (this.grabbed || e.button !== undefined && e.button !== 0 || isDesktop || isMiniGallery) {
			return;
		}
		if (!e.touches || !e.touches.length) {
			e.preventDefault();
		}
		const target = this.contentWrapper;
		target.style.transition = 'none';
		let eventSource = e.touches && e.touches[0] || e;
		let pos = eventSource.pageX || 0;
		this.grabbed = {
			x: pos,
			y: eventSource.pageY || 0,
			previous: pos,
			lastEvent: Date.now()
		};
	}
	onDrag(e) {
		const isDesktop = this.props.client?.isDesktopSize;
		const { isMiniGallery } = this.props;
		if (!this.grabbed || isDesktop || isMiniGallery) {
			return;
		}
		const target = this.contentWrapper;
		let eventSource = e.touches && e.touches[0] || e;
		let x = eventSource.pageX || 0;
		let y = eventSource.pageY || 0;
		let deltaX = Math.abs(x - this.grabbed.x);
		let deltaY = Math.abs(y - this.grabbed.y);
		target.style.transform = `translate(${x - this.grabbed.x}px, -50%)`;
		if (!this.grabbed.active && deltaX < TOUCH_SLOP) {
			if (deltaY >= TOUCH_SLOP) {
				// Vertical swipe
				this.grabbed = false;
			}
			return;
		}
		if (!this.grabbed.active) {
			this.grabbed.x = x;
			this.grabbed.active = true;
		}
		if (e.cancelable) {
			e.preventDefault();
		}
		this.grabbed.lastEvent = Date.now();
		this.grabbed.speed = this.grabbed.previous - x;
		this.grabbed.previous = x;
	}
	onDragEnd(e) {
		const isDesktop = this.props.client?.isDesktopSize;
		const { isMiniGallery } = this.props;
		let grabbed = this.grabbed;
		this.grabbed = false;
		if (grabbed) {
			if (!grabbed.active || isDesktop || isMiniGallery) {
				this.onImageClick(e);
				return;
			}
			const target = this.contentWrapper;
			let speed = grabbed.speed * Math.max(0, 1 - (Date.now() - grabbed.lastEvent) / 200);
			if (Math.abs(speed) > 3) {
				this.changeImageRelative(speed > 0 ? 1 : -1, { userTriggered: true });
				target.style.transform = `translate(${speed > 0 ? 100 : -100}%, -50%)`;
			}
			this.changeImageTimeout = window.setTimeout(() => {
				target.style.transition = `.3s transform`;
				target.style.transform = `translate(0, -50%)`;
			}, 10);
		} else {
			this.contentWrapper.style.transition = `.3s transform`;
			this.contentWrapper.style.transform = `translate(0, -50%)`;
		}

	}

	onImageClick(e) {
		// this.changeImageRelative(e.offsetX >= e.target.width * .5 ? 1 : -1);
	}

	changeImageRelative(offset, opts) {
		const hash = typeof window !== 'undefined' && location.hash.slice(1);
		let images = this.state.images || [];
		if (images.length === 1) return;

		const activeImage = this.getActiveImage();
		let activeImageIndex = images.findIndex(img => img.key === activeImage.key);
		if (activeImageIndex === -1) {
			activeImageIndex = 0;
		}
		activeImageIndex += offset;
		while (activeImageIndex < 0) {
			activeImageIndex += images.length;
		}
		while (activeImageIndex >= images.length) {
			activeImageIndex -= images.length;
		}

		this.setState({ direction: offset });

		this.changeActiveImage(images[activeImageIndex], opts);
		Store.emit('GALLERY_VIEW_IMAGE', images[activeImageIndex].key);
	}

	download(image, link) {
		const isEdge = accessNested(this.props, 'client.isEdge', false);
		const isIOS = accessNested(this.props, 'client.isIOS', false);
		link.target = '_blank';
		if (isIOS) {
			link.target = '_blank';
		} else if (!isEdge) {
			const c = document.createElement('canvas');
			const context = c.getContext('2d');
			c.width = image.naturalWidth;
			c.height = image.naturalHeight;
			context.drawImage(image, 0, 0);
			link.href = c.toDataURL('image/jpeg');
			link.style.display = 'none';
		}

		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
	}

	downloadImage(image, index) {
		let galleryImage = this.$galleryImage;
		const movieSlug = accessNested(this.props, 'appData.meta.title.localeSlug', 'image');
		const isIOS = accessNested(this.props, 'client.isIOS', false);
		const src = image.pdfSrc || this.getSrc(image);
		let link = document.createElement('a');
		const isGIF = src.endsWith('gif');


		if ((isGIF || image.pdfSrc) && !isIOS) {

			return this.downloadGifOrPdf(
				isGIF ? src : image.pdfSrc,
				(index ? `${movieSlug}-${index}.${isGIF ? 'gif' : 'pdf'}` : `${movieSlug}.${isGIF ? 'gif' : 'pdf'}`)
			);
		}

		link.download = index ? `${movieSlug}-${index}.jpg` : `${movieSlug}.jpg`;
		link.href = src;

		if (!galleryImage) {
			return;
		}

		if (index !== undefined) {
			galleryImage = document.createElement('IMG');
			galleryImage.crossOrigin = 'anonymous';
			galleryImage.src = (src + (src.includes('?') ? '&' : '?') + '_cors=1');
			galleryImage.onload = () => {
				this.download(galleryImage, link);
			};
			return;
		}

		this.download(galleryImage, link);
	}

	async downloadGifOrPdf(src, url) {
		const result = await fetch(src);
		const blob = await result.blob();

		const link = document.createElement('a');
		link.href = window.URL.createObjectURL(blob);
		link.download = url;

		link.dataset.downloadurl = ['application/octet-stream', link.download, link.href].join(':');
		link.click();
	}

	downloadAll() {
		this.state.images.forEach((img, key) => this.downloadImage(img, key));
		Store.emit('GALLERY_DOWNLOAD_ALL', this.props.id);
	}

	async downloadAllAsSingleFile({ path, fileName }) {
		const isGIF = path.endsWith('.gif');
		const isPDF = path.endsWith('.pdf');
		if (!(isGIF || isPDF)) {
			return;
		}

		Store.emit('GALLERY_DOWNLOAD_ALL', this.props.id);
		this.setState({ downloadAllInProgress: true });
		await this.downloadGifOrPdf(path, fileName);
		this.setState({ downloadAllInProgress: false });
	}

	togglePlaying(boolean) {
		this.setState({ playing: boolean });
	}

	triggerImageClick(path) {
		if (this.props.isMiniGallery) {
			Store.emit('TOUT_CLICK', 'carousel', 'tout', path);
		}

		if (path.startsWith('http')) {
			return;
		}
		// Use navigateInternally from utils/bolt ?
		if (path.startsWith('@')) {
			return Store.emit('OPEN_RESERVED_VARIABLE_LINK', path);
		}
		if (isVideosPage(path)) {
			return Store.emit('OPEN_VIDEO_OVERLAY', path);
		}
		if (path === 'boltShowtimes') {
			return Store.emit('SHOWTIMES_SHOW', true);
		}
		if (path === 'boltHomeEnt') {
			return Store.emit('HOMEENT_SHOW', true);
		}
		return Store.emit('CHANGE_ACTIVE_SECTION', path);
	}

	onImageShare(shareMetaOverrides) {
		const activeImage = this.getActiveImage();
		const imageUrl = this.getSrc(activeImage);
		const siteUrl = window.location.href;
		Store.emit('GALLERY_SHARE_IMAGE', {
			imageUrl,
			siteUrl,
			metaOverrides: shareMetaOverrides
		});
	}

	toggleShare() {
		Store.emit('CLOSE_GALLERY_SHARE');
	}

	startRotation() {
		const { id, isMiniGallery } = this.props;
		let autoRotate = accessNested(this.props, `appData.pages.${id}.data.autoRotate`, false);
		if (isMiniGallery) {
			autoRotate = accessNested(this.props, `appData.options.paramountFeatures.toutCarousel.autoRotate`, false);
		}
		this.rotating = true;

		if (!autoRotate || autoRotate <= 0) {
			return;
		}

		this.rotationInterval = window.setInterval(() => {
			this.changeImageRelative(1);
		}, autoRotate * 1000);
	}

	onLoadedHandler = () => {
		this.setState({ loading: false });
	}

	onEndedHandler = (activeVideo) => {
		const nextMediaContent = this.getNextVideo(activeVideo, this.state.images);
		if (nextMediaContent) {
			this.changeImageRelative(1);
		} else {
			Store.emit('VIDEO_ENDED', activeVideo);
		}
	}

	videoClickHandler() {
		if (!this.videoPlayer) {
			return;
		}

		if (!this.state.videoPlaying) {
			this.videoPlayer.play();
		}
	}

	getNextVideo = (activeVideo, videos) => {
		if (!activeVideo || !videos.length) {
			return;
		}
		const activeVidIndex = videos.findIndex((vid) => vid.key === activeVideo.key);
		return videos[activeVidIndex + 1];
	}

	renderArrow(direction, offset) {
		const rightArrowAria = accessNested(this.props, 'appData.copy.accessibilityCopy.$RIGHT_ARROW_ARIA');
		const leftArrowAria = accessNested(this.props, 'appData.copy.accessibilityCopy.$LEFT_ARROW_ARIA');

		return (
			<Button
				class={joinClasses(s.arrowBtn, this.state.loading && s.disabled)}
				disabled={this.state.loading}
				trigger={() => this.changeImageRelative(offset, { userTriggered: true })}
				ariaLabel={direction === 'rightArrow' ? rightArrowAria : leftArrowAria}
			>
				<ArrowDown class={s[direction]} />
			</Button>
		);
	}

	renderNavDots(images, activeKey) {
		// const navDotsAria = accessNested(this.props, 'appData.copy.accessibilityCopy.$NAV_DOTS_ARIA');

		return (
			<div class={s.navDots}>
				{images.map((img, index) => {
					const activeDot = activeKey == img.key;
					const isFirst = index === 0;
					const isLast = index === images.length - 1;
					let { direction = 1 } = this.state;
					if (images.length === 2 && activeKey == 0) {
						direction = -1;
					}

					return (
						<div
							class={joinClasses(s.dotBtn, s.pointer)}
							onClick={() => this.changeActiveImage(img)}
						// key={img.key}
						// ariaLabel={navDotsAria.replace('<number>', img.order)}
						>
							<div class={joinClasses({ s, dot: true, activeDot, isFirst, isLast, goingLeft: direction === -1, goingRight: direction === 1 })} />
						</div>
					);
				})}
			</div>
		);
	}

	renderDownloadButtons(activeImage, canDownloadAll, canDownloadSingle, customDownloadAll, fontStyles = {}, isVideo) {
		const copy = accessNested(this.props, 'appData.copy', {});
		if (!canDownloadAll && !canDownloadSingle) {
			return;
		}
		// const isIOS = accessNested(this.props, 'client.isIOS', false);
		// const isSafari = accessNested(this.props, 'client.isSafari', false);
		const isPDF = activeImage.pdfSrc;

		let downloadAllButton;
		if (canDownloadAll) {
			const useCustom = customDownloadAll?.path && customDownloadAll.fileName;
			const isIOS = this.props.client?.isIOS;
			const useDownloadAttribute = useCustom && isIOS;
			const downloadContent = [<span>{copy.baseCopy.$ALL || 'All'}</span>, <Download class={s.downloadIcon} />];
			const inProgress = this.state.downloadAllInProgress;
			const downloadAttrs = {
				class: joinClasses(s.downloadButton, s.downloadAll, isVideo && s.lowerOpacity, inProgress && s.downloadInProgress),
				disabled: isVideo || inProgress,
				key: 'all',
				ariaLabel: copy.accessibilityCopy.$DOWNLOAD_ALL_ARIA
			};
			if (useDownloadAttribute) {
				downloadAllButton = (
					<a {...downloadAttrs} target="_blank" href={customDownloadAll.path} download={customDownloadAll.fileName}>
						{downloadContent}
					</a>
				);
			} else {
				const trigger = useCustom ? () => this.downloadAllAsSingleFile(customDownloadAll) : this.downloadAll;
				downloadAllButton = (
					<Button {...downloadAttrs} trigger={trigger}>{downloadContent}</Button>
				);
			}
		}

		return (
			<div
				class={joinClasses(s.downloadBtnContainer,
					fontStyles.galleryDownloadButtons)}
				key="download-buttons"
			>
				{canDownloadSingle && (
					<Button
						class={joinClasses(s.downloadButton, s.downloadSingle, isVideo && s.lowerOpacity)}
						disabled={isVideo}
						trigger={() => {
							this.downloadImage(activeImage);
							Store.emit('GALLERY_DOWNLOAD_IMAGE', activeImage.key);
						}}
						key="single"
						ariaLabel={copy.accessibilityCopy.$DOWNLOAD_IMAGE_ARIA}
					>
						<span>{(isPDF ? (copy.baseCopy.$PDF || 'PDF') : (copy.baseCopy.$IMAGE || 'Image'))}</span>
						<Download class={s.downloadIcon} />
					</Button>
				)}
				{downloadAllButton}
			</div>
		);
	}

	renderImageLoader() {
		return <div class={s.spinner} key="loader" />;
	}

	imageLoaded() {
		this.setState({ loading: false });
	}

	onMarkdownClickCapture() { }

	renderImage() {
		const { loading, fadeOut } = this.state;
		const { isMiniGallery } = this.props;
		const activeImage = this.getActiveImage();
		const image = this.getSrc(activeImage);
		const fallbackFormat = this.getFallbackFormat();

		let elem = (
			<webp.Picture
				class={joinClasses(s.activeImage, loading && s.loading, activeImage.path && s.clickable, isMiniGallery && (fadeOut ? s.fadeOut : s.fadeIn))}
				src={image}
				fallbackFormat={fallbackFormat}
				alt={activeImage.alt}
				ariaLabel={activeImage.alt}
				onLoad={this.imageLoaded}
				ref={$ref => this.$galleryImage = $ref}
				key="image"
				onMouseDown={this.onDragStart}
				onTouchStart={this.onDragStart}
				onMouseMove={this.onDrag}
				onTouchMove={this.onDrag}
				onMouseUp={this.onDragEnd}
				onTouchEnd={this.onDragEnd}
				onClick={!!activeImage.path && (() => this.triggerImageClick(activeImage.path, activeImage.sameWindow))}
			/>
		);
		if (activeImage.path?.startsWith('http')) {
			return (
				<Link href={activeImage.path} target={activeImage.sameWindow ? '_self' : '_blank'}>
					{elem}
				</Link>
			);
		}
		return elem;
	}

	renderVideo() {
		const { loading, playing } = this.state;
		const video = this.getActiveImage();
		const isYoutube = video.youtubeId;
		const pageId = this.props?.id;
		const pageData = this.props?.appData?.pages[pageId]?.data || {};

		return (
			<div
				class={joinClasses(s.videoContainer, loading && s.loading)}
				ref={ref => this.videoContainer = ref}
				onMouseDown={!isYoutube && this.onDragStart}
				onTouchStart={!isYoutube && this.onDragStart}
				onMouseMove={!isYoutube && this.onDrag}
				onTouchMove={!isYoutube && this.onDrag}
				onMouseUp={!isYoutube && this.onDragEnd}
				onTouchEnd={!isYoutube && this.onDragEnd}
			>
				<VideoPlayer
					ref={ref => this.videoPlayer = ref}
					activeVideo={video}
					onPlay={() => this.togglePlaying(true)}
					onPause={() => this.togglePlaying(false)}
					noAutoplay={video.noAutoplay}
					onLoaded={this.onLoadedHandler}
					onEnded={() => this.onEndedHandler(video)}
					overrideStyles
					muted={pageData.autoMuteVideos}
				/>
				{isYoutube && (
					<div
						onMouseDown={this.onDragStart}
						onTouchStart={this.onDragStart}
						onMouseMove={this.onDrag}
						onTouchMove={this.onDrag}
						onMouseUp={this.onDragEnd}
						onTouchEnd={this.onDragEnd}
						onClick={this.videoClickHandler}
						class={s.touchOverlay}
						style={{ pointerEvents: playing ? 'none' : 'all' }}
					/>
				)}
			</div>
		);
	}

	renderPDF() {
		const pdf = this.getActiveImage();
		const hasThumbnail = pdf.desktopSrc || pdf.mobileSrc || pdf.tabletSrc;

		if (hasThumbnail) {
			return this.renderImage();
		}

		let embed = (
			<embed
				type="application/pdf"
				onClick={pdf.path && (() => this.triggerImageClick(pdf.path, pdf.sameWindow))}
				src={pdf.pdfSrc}
				width="100%"
				height="100%"
				onLoad={this.imageLoaded}
				ariaLabel={pdf.ariaLabel}
				onMouseDown={this.onDragStart}
				onTouchStart={this.onDragStart}
				onMouseMove={this.onDrag}
				onTouchMove={this.onDrag}
				onMouseUp={this.onDragEnd}
				onTouchEnd={this.onDragEnd}
			/>
		);

		if (pdf.path?.startsWith('http')) {
			return (
				<Link class={s.activeImage} href={pdf.path} target={pdf.sameWindow ? '_self' : '_blank'}>
					{embed}
				</Link>
			);
		}

		return (
			<div class={s.activeImage}>
				embed
			</div>
		);
	}

	renderContent(activeImage, isVideo) {
		let type = isVideo ? 'video' : (activeImage.pdfSrc ? 'pdf' : 'image');
		switch (type) {
			case 'video':
				return this.renderVideo();
			case 'pdf':
				return this.renderPDF();
			case 'image':
				return this.renderImage();
		}
	}

	renderShare(isVideo, shareMetaOverrides) {
		const activeImage = this.getActiveImage();
		const imageUrl = this.getSrc(activeImage);
		const baseCopy = this.props?.appData?.copy?.baseCopy || {};
		const accessibilityCopy = this.props?.appData?.copy?.accessibilityCopy || {};
		const shareCopy = baseCopy.$GALLERY_SHARE || baseCopy.$SHARE || 'Share';
		return (
			<div class={s.share}>
				<Button
					class={isVideo && s.lowerOpacity}
					trigger={() => this.onImageShare(shareMetaOverrides)}
					key="image-share"
					ariaLabel={accessibilityCopy.$GALLERY_SHARE_ARIA}
					disabled={isVideo}
				>
					{shareCopy}
				</Button>
				<SharePopout
					extraClass={s.sharePopout}
					appData={this.props.appData}
					shareOpen={this.props.gallery.imageShareLink}
					toggleSharePop={this.toggleShare}
					shareUrl={this.props.gallery.imageShareLink}
					shareImage={imageUrl}
					key="gallery-share"
				/>
			</div>
		);
	}

	render(props) {
		let path = `appData.pages.${props.id}.data`;
		if (props.isEmbeded) {
			path = `${path}.gallery`;
		}
		const data = props.pageData || accessNested(props, path) || {};
		const { images, loading } = this.state;
		const isDesktopSize = accessNested(props, 'client.dimensions.width', 0) >= 1024;
		const activeImage = this.getActiveImage();
		const isVideo = (activeImage.videoSrc || activeImage.vimeoId || activeImage.youtubeId
			|| activeImage.youtubeId || activeImage.disneySrc || activeImage.youtubePlaylistId);
		const galleryTitle = data.title;
		const canDownloadAll = data.downloadAll;
		const customDownloadAll = data.customDownloadAll || {};
		const canDownloadSingle = data.downloadSingle;
		const canShare = data.share;
		const galleryDescription = data.description;
		const hideArrowsMobile = data.hideArrowsMobile || props.isMiniGallery;
		const hideArrowsDesktop = data.hideArrowsDesktop || props.isMiniGallery;
		const shareMetaOverrides = data.shareMetaOverrides;
		const shouldShowControls = images.length > 1;
		const showMobileArrows = shouldShowControls && !isDesktopSize;
		const active = props.store?.header?.activeSection === props.id;
		const fontStyles = accessNested(props, 'appData.styling.fontVariables', {});
		const hasGif = this.state.images.map(image => this.getSrc(image)).find(src => src && src.endsWith('gif'));
		const isIOS = accessNested(props, 'client.isIOS', false);
		const isSafari = accessNested(props, 'client.isSafari', false);
		const iosChromeGif = isIOS && !isSafari && hasGif;
		const isSection = accessNested(this.props, ['store', 'appData', 'pages', props.id, 'section']);
		const dedicatedPage = pages[props.id] && !isSection;
		const hasDownloads = (canDownloadAll || canDownloadSingle) && !iosChromeGif;
		const hasExtraActions = hasDownloads || canShare;

		return (
			<div
				class={joinClasses({
					s, container: true,
					active: active,
					hasDownloads: hasDownloads,
					canShare: canShare,
					includeTopArrows: !isDesktopSize && !hideArrowsMobile,
					isEmbeded: props.isEmbeded,
					dedicatedPage,
					miniGallery: props.isMiniGallery
				})}
				data-section={props.id}
				ref={$ref => this.$container = $ref}
			>
				{this.renderBgContent()}
				<div class={s.inner}>
					{galleryTitle && <HTML tag="h1" markdown content={galleryTitle} class={joinClasses(s.galleryTitle, 'h3')} />}
					{galleryDescription && <HTML tag="h2" onClickCapture={this.onMarkdownClickCapture} markdown content={galleryDescription} class={joinClasses(s.galleryDescription, 'xlarge')} />}
					<div class={s.imageWrapper} key="imageWrapper" id={`${props.id}GalleryImageWrapper`}>
						{shouldShowControls && (
							<div
								class={joinClasses(s.arrows,
									showMobileArrows && s.mobileArrows,
									!hasDownloads && s.noDownloads,
									hideArrowsMobile && s.hideArrowsMobile,
									hideArrowsDesktop && s.hideArrowsDesktop
								)}
								key="arrows"
							>
								{this.renderArrow('leftArrow', -1)}
								{this.renderArrow('rightArrow', 1)}
							</div>
						)}
						<div class={s.contentWrapper} ref={ref => this.contentWrapper = ref}>
							{this.renderContent(activeImage, isVideo)}
						</div>
						{loading && this.renderImageLoader()}
					</div>
					<div class={s.bottomWrapper} key="bottomWrapper">
						<div class={s.bottom} key="bottom">
							{shouldShowControls && this.renderNavDots(images, activeImage.key)}
							{activeImage.annotation && (
								<HTML class={joinClasses(s.annotation, fontStyles.galleryAnnotations)} content={activeImage.annotation} markdown />
							)}
							{hasExtraActions && (
								<div class={s.extraActions}>
									{canShare && this.renderShare(isVideo, shareMetaOverrides)}
									{!iosChromeGif && this.renderDownloadButtons(activeImage, canDownloadAll, canDownloadSingle, customDownloadAll, fontStyles, isVideo)}
								</div>
							)}
						</div>
					</div>
				</div>
				{this.renderOverlayedContent()}
			</div>
		);
	}
}
